import React, { useEffect, useState } from 'react';
import { useModal } from '../hooks/useModal';
import { useCarousel } from '../hooks/useCarousel';
import { API_ENDPOINTS } from '../constants/api';
import { CAROUSEL_CONFIG } from '../constants/config';

const Gallery = () => {
  const { selectedItem: selectedImage, openModal, closeModal } = useModal();
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    const loadGalleryImages = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.galleryImages);
        const filenames = await response.json();
        const images = filenames.map((filename, index) => ({
          id: index + 1,
          src: `/images/gallery/${filename}`,
          alt: filename.replace(/\.[^/.]+$/, ''),
          name: filename.replace(/\.[^/.]+$/, '')
        }));
        setGalleryImages(images);
      } catch (error) {
        console.error('Failed to load gallery images:', error);
      }
    };
    loadGalleryImages();
  }, []);
  const { currentIndex, nextSlide, prevSlide, goToSlide, resumeAutoRotate } = useCarousel(
    galleryImages.length,
    CAROUSEL_CONFIG.autoRotateInterval,
    CAROUSEL_CONFIG.pauseSlides
  );

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    if (selectedImage) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedImage, closeModal]);

  const currentImage = galleryImages.length > 0 ? galleryImages[currentIndex] : null;

  const handleOpenModal = (image) => {
    const index = galleryImages.findIndex(img => img.id === image.id);
    setSelectedImageIndex(index);
    openModal(image);
  };

  const handleNextImage = () => {
    const nextIndex = (selectedImageIndex + 1) % galleryImages.length;
    setSelectedImageIndex(nextIndex);
    openModal(galleryImages[nextIndex]);
  };

  const handlePrevImage = () => {
    const prevIndex = (selectedImageIndex - 1 + galleryImages.length) % galleryImages.length;
    setSelectedImageIndex(prevIndex);
    openModal(galleryImages[prevIndex]);
  };

  return (
    <div className="gallery">
      <div className="container">
        <h1>Photo Gallery</h1>
        <p className="gallery-intro">
          Explore the beautiful destinations and luxury amenities that await you on our tours
        </p>
      </div>

      <div className="gallery-hero">
        {currentImage && (
          <div className="carousel-container">
            <img
              src={currentImage.src}
              alt={currentImage.alt}
              className="carousel-image"
              onClick={() => handleOpenModal(currentImage)}
            />
            <div className="carousel-name">
              <h2>{currentImage.name}</h2>
            </div>

          <button
            className="carousel-button carousel-prev"
            onClick={prevSlide}
            aria-label="Previous image"
          >
            ‹
          </button>
          <button
            className="carousel-button carousel-next"
            onClick={nextSlide}
            aria-label="Next image"
          >
            ›
          </button>

            <div className="carousel-indicators">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  onMouseEnter={resumeAutoRotate}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={index === currentIndex}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="container">
        <div className="gallery-credit">
          <p>
            Photography by <a href="https://www.iainizattphotography.co.uk" target="_blank" rel="noopener noreferrer">Iain Izatt</a>
            {' | '}
            <a href="https://www.instagram.com/iainizatt_photography" target="_blank" rel="noopener noreferrer">@iainizatt_photography</a>
          </p>
        </div>

        <div className="gallery-grid">
          {galleryImages.map((image) => (
            <div
              key={image.id}
              className="gallery-item"
              onClick={() => handleOpenModal(image)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOpenModal(image);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`View ${image.alt}`}
            >
              <img src={image.src} alt={image.alt} loading="lazy" />
              <div className="gallery-overlay">
                <span className="gallery-name">{image.name}</span>
              </div>
              <div className="gallery-caption">
                <p>{image.name}</p>
              </div>
            </div>
          ))}
        </div>

        {selectedImage && (
          <div
            className="modal-overlay"
            onClick={closeModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button
                className="modal-close"
                onClick={closeModal}
                aria-label="Close modal"
              >
                &times;
              </button>
              <button
                className="modal-nav-button modal-prev"
                onClick={handlePrevImage}
                aria-label="Previous image"
              >
                ‹
              </button>
              <button
                className="modal-nav-button modal-next"
                onClick={handleNextImage}
                aria-label="Next image"
              >
                ›
              </button>
              <img src={selectedImage.src} alt={selectedImage.alt} />
              <div className="modal-info">
                <h3 id="modal-title">{selectedImage.name}</h3>
                <a
                  href={`https://www.google.com/maps/search/${encodeURIComponent(selectedImage.name + ', Scotland')}/?zoom=10`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="modal-map-button"
                >
                  View Location on Google Maps
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;